import { supportsAutomaticOAuth } from "./hooks/serverMetadata";
import { useSdkClient, useSlugs } from "@/contexts/Sdk";
import {
  createDefaultMcpEndpoint,
  DEFAULT_ENDPOINT_FAILED_MESSAGE,
} from "@/lib/mcpEndpoints";
import { mcpServerRouteParam } from "@/lib/sources";
import { getServerURL } from "@/lib/utils";
import {
  useCatalogInstallations,
  type CatalogServer,
} from "@/pages/catalog/hooks";
import {
  collectibleHeaders,
  getRemoteDisplayInfo,
  isFigmaCatalogServer,
} from "@/pages/catalog/remotes";
import {
  configureCreatedRemoteMcpIdentity,
  type ConfigureCreatedIdentityResult,
  type RemoteMcpCreationIdentity,
} from "@/pages/sources/remote-mcp/configureCreatedIdentity";
import type { Gram } from "@gram/client";
import type { ServiceAccountClientSpec } from "@/lib/remote-identity";
import type { RequestOptions } from "@gram/client/lib/sdks.js";
import type { ExternalMCPRemote } from "@gram/client/models/components/externalmcpremote.js";
import type { ExternalMCPRemoteHeader } from "@gram/client/models/components/externalmcpremoteheader.js";
import type { McpServer } from "@gram/client/models/components/mcpserver.js";
import { invalidateAllGetMcpMetadata } from "@gram/client/react-query/getMcpMetadata.js";
import { invalidateAllMcpEndpoints } from "@gram/client/react-query/mcpEndpoints.js";
import { invalidateAllMcpServers } from "@gram/client/react-query/mcpServers.js";
import { invalidateAllRiskListPolicies } from "@gram/client/react-query/riskListPolicies.js";
import { invalidateAllRemoteMcpServerHeaders } from "@gram/client/react-query/remoteMcpServerHeaders.js";
import { invalidateAllRemoteMcpServers } from "@gram/client/react-query/remoteMcpServers.js";
import { invalidateAllRemoteSessionClients } from "@gram/client/react-query/remoteSessionClients.js";
import { invalidateAllRemoteSessionIssuers } from "@gram/client/react-query/remoteSessionIssuers.js";
import { invalidateAllUnproxiedMcpServers } from "@gram/client/react-query/unproxiedMcpServers.js";
import { invalidateAllUserSessionIssuers } from "@gram/client/react-query/userSessionIssuers.js";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useEffectiveUserSessionIssuers } from "@/hooks/useEffectiveUserSessionIssuers";
import { useDetectorMode } from "@/pages/security/use-detector-mode";
import {
  buildServerGuardrailRequest,
  catalogPresetState,
  type ServerGuardrailState,
} from "@/pages/security/server-guardrails/server-guardrail-policy";

export const ISSUER_LOOKUP_FAILED_MESSAGE =
  "Couldn't load your organization's user session issuers. Try again.";

type InstallPhaseName =
  | "selectRemotes"
  | "configure"
  | "guardrails"
  | "installing"
  | "complete";

export interface ServerConfig {
  server: CatalogServer;
  name: string;
  /** Remote endpoints to install — one remote MCP server is created per URL. */
  remotes: ExternalMCPRemote[];
  /** User-entered header values, keyed by [headerValueKey]. */
  headerValues: Record<string, string>;
  identityMode: RemoteMcpCreationIdentity;
  agentAuthorization: string;
  /** An OAuth service account client to bind instead of agentAuthorization. */
  serviceAccountClient: ServiceAccountClientSpec | null;
}

/** Configuration for a server with multiple remotes during the selectRemotes phase */
interface MultiRemoteServerConfig {
  server: CatalogServer;
  name: string;
  remotes: ExternalMCPRemote[];
  selectedRemoteUrls: Set<string>;
}

/** How the optional guardrail for an install ended. */
type GuardrailOutcome =
  | { status: "created"; name: string }
  | { status: "failed"; name: string; error: string };

export interface ServerInstallStatus {
  key: string;
  name: string;
  /** The catalog entry this server was installed from, for its logo. */
  server?: CatalogServer;
  status: "pending" | "creating" | "completed" | "failed";
  /** mcp_servers row id — used by callers that bundle installs (e.g. plugins). */
  mcpServerId?: string;
  /** Route param (slug or id) for the MCP server details page. */
  mcpServerParam?: string;
  /** Public URL of the pre-staged default MCP endpoint, when one was created. */
  mcpEndpointUrl?: string;
  error?: string;
  /**
   * Set when the server was created but kept disabled because its identity
   * still needs a step in Settings. The install counts as failed for callers
   * that tally results, but the dialog shows it as a next step, not an error.
   */
  setupRequired?: string;
  identityMode?: RemoteMcpCreationIdentity;
  /** Exact remote endpoint selected for this installed server. */
  remoteUrl?: string;
}

interface WorkflowBase {
  projectSlug?: string;
  /**
   * Whether a remote MCP server already exists in the project for one of this
   * catalog server's endpoint URLs. Informational only — installing again just
   * creates another server.
   */
  isServerAlreadyInstalled: (server: CatalogServer) => boolean;
  reset: () => void;
}

export interface SelectRemotesPhase extends WorkflowBase {
  phase: "selectRemotes";
  /** Servers with multiple remotes that need configuration */
  multiRemoteConfigs: MultiRemoteServerConfig[];
  /** Index of the server currently being configured */
  currentServerIndex: number;
  /** Update the current server's name or selected remotes */
  updateCurrentConfig: (updates: {
    name?: string;
    selectedRemoteUrls?: Set<string>;
  }) => void;
  /** Move to the next multi-remote server, or to configure phase if done */
  nextServer: () => void;
  /** Whether the current server can proceed (has at least one remote selected) */
  canProceed: boolean;
}

export interface ConfigurePhase extends WorkflowBase {
  phase: "configure";
  serverConfigs: ServerConfig[];
  updateServerConfig: (
    index: number,
    updates: Partial<
      Pick<
        ServerConfig,
        "name" | "identityMode" | "agentAuthorization" | "serviceAccountClient"
      >
    >,
  ) => void;
  setHeaderValue: (
    index: number,
    remoteUrl: string,
    headerName: string,
    value: string,
  ) => void;
  canInstall: boolean;
  /**
   * Why the install cannot start regardless of user input. Set when the
   * user session issuer lookup failed: installing anyway would silently mint
   * a project-specific issuer instead of honouring the organization's. The
   * interactive dialog shows it; headless callers report it as a failure
   * instead of waiting on canInstall forever.
   */
  installBlockedReason?: string;
  startInstall: () => Promise<void>;
  /** Go back to selectRemotes phase (only available if there were multi-remote servers) */
  goBack?: () => void;
  /**
   * Set when the caller offers guardrails: moves on to the Guardrails phase
   * instead of installing right away.
   */
  continueToGuardrails?: (options?: { configureSkipped?: boolean }) => void;
}

/** What the Guardrails step shows about one server it would protect. */
export interface GuardrailServerSummary {
  key: string;
  name: string;
  registrySpecifier: string;
  iconUrl?: string;
  oauth: boolean;
}

export interface GuardrailsPhase extends WorkflowBase {
  phase: "guardrails";
  /** The guardrail being drafted, pre-filled with the catalog preset. */
  guardrail: ServerGuardrailState;
  updateGuardrail: (
    update: (state: ServerGuardrailState) => ServerGuardrailState,
  ) => void;
  /** The servers the guardrail will cover. Unproxied servers are left out:
   *  their traffic never passes through Speakeasy. */
  servers: GuardrailServerSummary[];
  /** Install and create the guardrail, scoped to every server that installs. */
  installWithGuardrail: () => Promise<void>;
  /** Install without a guardrail (the recommendation switched off). */
  skip: () => Promise<void>;
  /**
   * Install without a guardrail and resolve to the ids of the installed
   * servers Speakeasy proxies, so the caller can open the full policy editor
   * scoped to them.
   */
  installForCustomizing: () => Promise<string[]>;
  /** Back to the Configure phase. Absent when Configure had nothing to ask,
   *  so going back would only bounce forward again. */
  goBack?: () => void;
}

interface InstallingPhase extends WorkflowBase {
  phase: "installing";
  statuses: ServerInstallStatus[];
}

export interface CompletePhase extends WorkflowBase {
  phase: "complete";
  statuses: ServerInstallStatus[];
  /** Absent unless a guardrail was requested. */
  guardrail?: GuardrailOutcome;
}

export type RemoteMcpInstallWorkflow =
  | SelectRemotesPhase
  | ConfigurePhase
  | GuardrailsPhase
  | InstallingPhase
  | CompletePhase;

interface UseRemoteMcpInstallWorkflowOptions {
  servers: CatalogServer[];
  projectSlug?: string;
  serverNameSuffix?: string;
  /**
   * Install every endpoint of multi-remote servers instead of pausing on the
   * interactive selectRemotes phase. Required by headless/auto-start callers
   * (onboarding), which have no UI to select from.
   */
  autoSelectRemotes?: boolean;
  /**
   * Add a skippable Guardrails step between Configure and the install. Off for
   * headless callers, which have no UI to draft a policy in.
   */
  offerGuardrails?: boolean;
  /** Fresh catalog admission before any create, never after acceptance. */
  beforeInstall?: () => Promise<boolean>;
}

/** Key into [ServerConfig.headerValues] for one header of one remote. */
export function headerValueKey(remoteUrl: string, headerName: string): string {
  return `${remoteUrl} ${headerName.toLowerCase()}`;
}

function buildServerConfig(
  server: CatalogServer,
  serverNameSuffix = "",
): ServerConfig {
  return {
    server,
    name: `${server.title ?? server.registrySpecifier}${serverNameSuffix}`,
    remotes: server.remotes ?? [],
    headerValues: {},
    identityMode: supportsAutomaticOAuth(server) ? "user" : "none",
    agentAuthorization: "",
    serviceAccountClient: null,
  };
}

/**
 * One remote MCP server creation, resolved from a config at install start.
 * Multi-endpoint installs get per-endpoint names so the resulting servers are
 * tellable apart.
 */
interface InstallTarget {
  server: CatalogServer;
  remote: ExternalMCPRemote;
  name: string;
  headers: Array<{ header: ExternalMCPRemoteHeader; value: string }>;
  identityMode: RemoteMcpCreationIdentity;
  agentAuthorization: string;
  serviceAccountClient: ServiceAccountClientSpec | null;
}

function buildInstallTargets(config: ServerConfig): InstallTarget[] {
  return config.remotes.map((remote) => ({
    server: config.server,
    remote,
    name:
      config.remotes.length > 1
        ? `${config.name.replace(/_Governed$/, "")} ${getRemoteDisplayInfo(remote.url).name}_Governed`
        : config.name,
    headers: collectibleHeaders(remote).flatMap((header) => {
      const value =
        config.headerValues[headerValueKey(remote.url, header.name)]?.trim();
      return value ? [{ header, value }] : [];
    }),
    identityMode: config.identityMode,
    agentAuthorization: config.agentAuthorization,
    serviceAccountClient: config.serviceAccountClient,
  }));
}

/**
 * Best-effort: a logo failure never fails the install, and the returned
 * promise never rejects. Resolves true only when the logo actually landed on
 * mcp_metadata, so callers know whether a metadata refetch is warranted.
 */
async function persistServerIconBestEffort(
  client: Gram,
  target: InstallTarget,
  mcpServerId: string,
  reqOpts: RequestOptions | undefined,
): Promise<boolean> {
  const iconUrl = target.server.iconUrl;
  if (!iconUrl) return false;

  try {
    const uploaded = await client.assets.fetchImageFromURL(
      { fetchImageFromURLForm2: { url: iconUrl } },
      undefined,
      reqOpts,
    );
    await client.mcpMetadata.set(
      {
        setMcpMetadataRequestBody: {
          mcpServerId,
          logoAssetId: uploaded.asset.id,
        },
      },
      undefined,
      reqOpts,
    );
    return true;
  } catch (iconError) {
    console.warn("Failed to persist server icon during install.", {
      mcpServerId,
      iconUrl,
      iconError,
    });
    return false;
  }
}

/**
 * Installs one target as an unproxied MCP server instead of a Speakeasy-proxied
 * remote one: creates the unproxied_mcp_servers row, links an mcp_servers
 * wrapper (rolling back the former on failure, mirroring installTarget's own
 * remote-server path), and returns the same shape installTarget does. There
 * is no OAuth to auto-configure and no Speakeasy endpoint to pre-stage — the
 * customer connects straight to the vendor.
 */
async function installUnproxiedTarget(
  client: Gram,
  target: InstallTarget,
  reqOpts: RequestOptions | undefined,
): Promise<{
  mcpServer: McpServer;
  mcpEndpointUrl?: string;
  authConfigured: boolean;
  identityConfiguration?: ConfigureCreatedIdentityResult;
  iconPersistence: Promise<boolean>;
}> {
  const unproxiedMcpServer = await client.unproxiedMcp.createServer(
    {
      createUnproxiedMcpServerForm: {
        name: target.name,
        url: target.remote.url,
        description: target.server.description || undefined,
      },
    },
    undefined,
    reqOpts,
  );

  let mcpServer: McpServer;
  try {
    mcpServer = await client.mcpServers.create(
      {
        createMcpServerForm: {
          name: target.name,
          unproxiedMcpServerId: unproxiedMcpServer.id,
          // Unproxied servers have no Speakeasy-hosted endpoint, so
          // disabled/private/public gates nothing Speakeasy actually serves.
          visibility: "public",
        },
      },
      undefined,
      reqOpts,
    );
  } catch (linkError) {
    try {
      await client.unproxiedMcp.deleteServer(
        { id: unproxiedMcpServer.id },
        undefined,
        reqOpts,
      );
    } catch (rollbackError) {
      const linkMsg =
        linkError instanceof Error ? linkError.message : String(linkError);
      const rollbackMsg =
        rollbackError instanceof Error
          ? rollbackError.message
          : String(rollbackError);
      throw new Error(
        `Created unproxied MCP server ${unproxiedMcpServer.id} but failed to link an MCP server, and the rollback also failed. Delete it manually before retrying. Cause: ${linkMsg}. Rollback: ${rollbackMsg}.`,
      );
    }
    throw linkError instanceof Error ? linkError : new Error(String(linkError));
  }

  // Runs detached: a slow icon host must not hold up install completion. The
  // caller chains a metadata refetch onto the returned promise instead.
  const iconPersistence = persistServerIconBestEffort(
    client,
    target,
    mcpServer.id,
    reqOpts,
  );

  return {
    mcpServer,
    mcpEndpointUrl: undefined,
    authConfigured: false,
    iconPersistence,
  };
}

/**
 * Installs catalog servers as Remote MCP servers. For every selected remote
 * endpoint: create a remote_mcp_servers row, link an mcp_servers row (which
 * mints the server's user session issuer), persist any user-provided upstream
 * headers, auto-configure OAuth when the upstream advertises it, and pre-stage
 * a default MCP endpoint. Everything is a synchronous management API call —
 * there is no deployment to poll.
 */
export function useRemoteMcpInstallWorkflow({
  servers,
  projectSlug,
  autoSelectRemotes = false,
  serverNameSuffix = "",
  offerGuardrails = false,
  beforeInstall,
}: UseRemoteMcpInstallWorkflowOptions): RemoteMcpInstallWorkflow {
  const client = useSdkClient();
  const detectorMode = useDetectorMode();
  const queryClient = useQueryClient();
  const { orgSlug } = useSlugs();
  const issuerQuery = useEffectiveUserSessionIssuers({});
  const defaultOrganizationIssuerId = issuerQuery.organizationIssuers[0]?.id;

  // Same check as the catalog card, including toolset-backed installs.
  // Use the workflow's project when the caller is installing somewhere other
  // than the route's project.
  const { installState } = useCatalogInstallations(projectSlug);
  const isServerAlreadyInstalled = useCallback(
    (server: CatalogServer) => installState(server) === "added",
    [installState],
  );

  const [phase, setPhase] = useState<InstallPhaseName>("configure");
  const [serverConfigs, setServerConfigs] = useState<ServerConfig[]>([]);
  const [statuses, setStatuses] = useState<ServerInstallStatus[]>([]);
  const [guardrail, setGuardrail] = useState<ServerGuardrailState | null>(null);
  const [configureSkipped, setConfigureSkipped] = useState(false);
  const [guardrailOutcome, setGuardrailOutcome] = useState<
    GuardrailOutcome | undefined
  >(undefined);

  // State for multi-remote server selection
  const [multiRemoteConfigs, setMultiRemoteConfigs] = useState<
    MultiRemoteServerConfig[]
  >([]);
  const [currentServerIndex, setCurrentServerIndex] = useState(0);

  // Track last processed server index to prevent double-click duplicates
  const lastProcessedIndexRef = useRef(-1);

  // Keep a live handle on the current phase so the partition effect can bail
  // out once installation has started — a props refresh mid-install must not
  // snap the dialog back to a fresh configure step.
  const phaseRef = useRef<InstallPhaseName>("configure");
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  const partitionServers = useCallback(() => {
    const multiRemote: MultiRemoteServerConfig[] = [];
    const singleRemote: ServerConfig[] = [];

    for (const server of servers) {
      const remotes = server.remotes ?? [];
      if (remotes.length > 1 && !autoSelectRemotes) {
        multiRemote.push({
          server,
          name: `${server.title ?? server.registrySpecifier}${serverNameSuffix}`,
          remotes,
          selectedRemoteUrls: new Set(),
        });
      } else {
        singleRemote.push(buildServerConfig(server, serverNameSuffix));
      }
    }

    setMultiRemoteConfigs(multiRemote);
    setServerConfigs(singleRemote);
    setCurrentServerIndex(0);
    lastProcessedIndexRef.current = -1;
    setPhase(multiRemote.length > 0 ? "selectRemotes" : "configure");
  }, [autoSelectRemotes, serverNameSuffix, servers]);

  // Initialize server configs when servers change - partition into multi/single remote.
  useEffect(() => {
    if (
      phaseRef.current !== "selectRemotes" &&
      phaseRef.current !== "configure"
    ) {
      return;
    }
    partitionServers();
  }, [partitionServers]);

  const updateServerConfig = useCallback(
    (
      index: number,
      updates: Partial<
        Pick<
          ServerConfig,
          | "name"
          | "identityMode"
          | "agentAuthorization"
          | "serviceAccountClient"
        >
      >,
    ) => {
      setServerConfigs((prev) =>
        prev.map((config, i) => {
          if (i !== index) return config;
          return { ...config, ...updates };
        }),
      );
    },
    [],
  );

  const setHeaderValue = useCallback(
    (index: number, remoteUrl: string, headerName: string, value: string) => {
      setServerConfigs((prev) =>
        prev.map((config, i) => {
          if (i !== index) return config;
          return {
            ...config,
            headerValues: {
              ...config.headerValues,
              [headerValueKey(remoteUrl, headerName)]: value,
            },
          };
        }),
      );
    },
    [],
  );

  // Callbacks for multi-remote selection phase
  const updateCurrentConfig = useCallback(
    (updates: { name?: string; selectedRemoteUrls?: Set<string> }) => {
      setMultiRemoteConfigs((prev) =>
        prev.map((config, i) => {
          if (i !== currentServerIndex) return config;
          return {
            ...config,
            ...(updates.name !== undefined && {
              name: updates.name,
            }),
            ...(updates.selectedRemoteUrls !== undefined && {
              selectedRemoteUrls: updates.selectedRemoteUrls,
            }),
          };
        }),
      );
    },
    [currentServerIndex],
  );

  const canProceed = useMemo(() => {
    const currentConfig = multiRemoteConfigs[currentServerIndex];
    if (!currentConfig) return false;
    return (
      currentConfig.name.trim() !== "" &&
      currentConfig.selectedRemoteUrls.size > 0
    );
  }, [multiRemoteConfigs, currentServerIndex]);

  const nextServer = useCallback(() => {
    if (!canProceed) return;

    // Prevent double-click duplicates by checking if this index was already processed
    if (lastProcessedIndexRef.current === currentServerIndex) return;
    lastProcessedIndexRef.current = currentServerIndex;

    const currentConfig = multiRemoteConfigs[currentServerIndex];
    if (currentConfig) {
      setServerConfigs((prev) => [
        ...prev,
        {
          server: currentConfig.server,
          name: currentConfig.name,
          remotes: currentConfig.remotes.filter((r) =>
            currentConfig.selectedRemoteUrls.has(r.url),
          ),
          headerValues: {},
          identityMode: supportsAutomaticOAuth(currentConfig.server)
            ? "user"
            : "none",
          agentAuthorization: "",
          serviceAccountClient: null,
        },
      ]);
    }

    if (currentServerIndex < multiRemoteConfigs.length - 1) {
      setCurrentServerIndex((prev) => prev + 1);
    } else {
      // Done with all multi-remote servers, move to configure phase
      setPhase("configure");
    }
  }, [canProceed, currentServerIndex, multiRemoteConfigs]);

  // The issuer lookup decides whether the install can honour an organization
  // issuer at all. A failed or truncated listing is not "no organization
  // issuers": falling through to the project-specific default would silently
  // diverge from the organization's policy, so block instead.
  const installBlockedReason = issuerQuery.isError
    ? ISSUER_LOOKUP_FAILED_MESSAGE
    : undefined;

  // A config with no HTTP remote cannot be installed as a remote MCP server —
  // startInstall reports it as failed. At least one config must be
  // installable for the install to be worth starting at all.
  const canInstall = useMemo(() => {
    return (
      serverConfigs.length > 0 &&
      !issuerQuery.isLoading &&
      installBlockedReason === undefined &&
      serverConfigs.every((c) => c.name.trim() !== "") &&
      serverConfigs.every(
        (c) =>
          c.identityMode !== "agent" ||
          c.agentAuthorization.trim() !== "" ||
          c.serviceAccountClient !== null,
      ) &&
      serverConfigs.some((c) => c.remotes.length > 0)
    );
  }, [installBlockedReason, issuerQuery.isLoading, serverConfigs]);

  // goBack returns to selectRemotes phase - only available if there were multi-remote servers
  const goBack = useCallback(() => {
    // Remove multi-remote servers from serverConfigs; they re-enter via
    // nextServer. Single-remote configs are the ones whose server has 0 or 1
    // remotes overall.
    setServerConfigs((prev) =>
      prev.filter((config) => (config.server.remotes ?? []).length <= 1),
    );
    setCurrentServerIndex(0);
    lastProcessedIndexRef.current = -1; // Reset to allow re-processing
    setPhase("selectRemotes");
  }, []);

  const hasMultiRemoteServers = multiRemoteConfigs.length > 0;

  const installTarget = useCallback(
    async (
      target: InstallTarget,
      reqOpts: RequestOptions | undefined,
    ): Promise<{
      mcpServer: McpServer;
      mcpEndpointUrl?: string;
      authConfigured: boolean;
      identityConfiguration?: ConfigureCreatedIdentityResult;
      iconPersistence: Promise<boolean>;
    }> => {
      if (isFigmaCatalogServer(target.server)) {
        return installUnproxiedTarget(client, target, reqOpts);
      }

      const probe = await client.remoteMcp.probeURL(
        { probeURLForm: { url: target.remote.url } },
        undefined,
        reqOpts,
      );
      if (
        probe.outcome === "unreachable" ||
        probe.outcome === "invalid_mcp_response"
      ) {
        throw new Error(
          probe.outcome === "unreachable"
            ? "The Remote MCP server is unreachable."
            : "The URL did not return a valid MCP response.",
        );
      }
      const remoteMcpServer = await client.remoteMcp.createServer(
        {
          createServerForm: {
            name: target.name,
            url: target.remote.url,
            transportType: "streamable-http",
          },
        },
        undefined,
        reqOpts,
      );

      let mcpServer: McpServer;
      try {
        mcpServer = await client.mcpServers.create(
          {
            createMcpServerForm: {
              name: target.name,
              remoteMcpServerId: remoteMcpServer.id,
              userSessionIssuerId: defaultOrganizationIssuerId,
              // Catalog installs are noninteractive, so prefer the first
              // organization issuer and retain the project-specific fallback
              // when the organization has none.
              // Identity setup is completed before this is made available.
              visibility: "disabled",
            },
          },
          undefined,
          reqOpts,
        );
      } catch (linkError) {
        try {
          await client.remoteMcp.deleteServer(
            { id: remoteMcpServer.id },
            undefined,
            reqOpts,
          );
        } catch (rollbackError) {
          const linkMsg =
            linkError instanceof Error ? linkError.message : String(linkError);
          const rollbackMsg =
            rollbackError instanceof Error
              ? rollbackError.message
              : String(rollbackError);
          throw new Error(
            `Created remote MCP server ${remoteMcpServer.id} but failed to link an MCP server, and the rollback also failed. Delete it manually before retrying. Cause: ${linkMsg}. Rollback: ${rollbackMsg}.`,
          );
        }
        throw linkError instanceof Error
          ? linkError
          : new Error(String(linkError));
      }

      // Persist user-provided upstream headers. Best-effort per header: the
      // server is already linked and headers can always be (re)configured from
      // its Settings tab, so a failure warns instead of failing the install.
      for (const { header, value } of target.headers) {
        try {
          await client.remoteMcp.createServerHeader(
            {
              createServerHeaderForm: {
                remoteMcpServerId: remoteMcpServer.id,
                name: header.name,
                description: header.description,
                isSecret: header.isSecret ?? false,
                isRequired: header.isRequired ?? false,
                value,
              },
            },
            undefined,
            reqOpts,
          );
        } catch (headerError) {
          console.warn("Failed to save upstream header during install.", {
            remoteMcpServerId: remoteMcpServer.id,
            header: header.name,
            headerError,
          });
          toast.warning(
            `Couldn't save the "${header.name}" header for ${target.name}. Add it from the server's Settings tab.`,
          );
        }
      }

      // Runs detached: a slow icon host must not hold up install completion.
      // startInstall chains a metadata refetch onto the returned promise.
      const iconPersistence = persistServerIconBestEffort(
        client,
        target,
        mcpServer.id,
        reqOpts,
      );

      const identityConfiguration = await configureCreatedRemoteMcpIdentity({
        client,
        remoteMcpServer,
        mcpServer,
        identityMode: target.identityMode,
        // Already the whole header value: the credential form assembles the
        // Bearer/Basic prefix so the operator can see exactly what is sent.
        agentAuthorization:
          target.identityMode === "agent"
            ? target.agentAuthorization.trim()
            : undefined,
        serviceAccountClient:
          target.identityMode === "agent"
            ? (target.serviceAccountClient ?? undefined)
            : undefined,
        options: reqOpts,
      });
      const configuredMcpServer =
        identityConfiguration.status === "configured"
          ? identityConfiguration.mcpServer
          : mcpServer;

      // Pre-stage a default endpoint so the user doesn't have to create one
      // before the server can serve. Best-effort: never rolls back the source.
      if (!orgSlug) {
        toast.warning(DEFAULT_ENDPOINT_FAILED_MESSAGE);
      }
      const endpoint = orgSlug
        ? await createDefaultMcpEndpoint(
            client,
            configuredMcpServer,
            orgSlug,
            reqOpts,
          )
        : undefined;

      return {
        mcpServer: configuredMcpServer,
        mcpEndpointUrl: endpoint
          ? `${getServerURL()}/mcp/${endpoint.slug}`
          : undefined,
        // A bound service account client is new auth state too, so the
        // session-client lists are refreshed for it as well.
        authConfigured:
          !!identityConfiguration.userIdentity?.status ||
          (target.identityMode === "agent" &&
            !!target.serviceAccountClient &&
            identityConfiguration.status === "configured"),
        identityConfiguration,
        iconPersistence,
      };
    },
    [client, defaultOrganizationIssuerId, orgSlug],
  );

  const admissionInFlight = useRef(false);
  const admissionGeneration = useRef(0);
  useEffect(
    () => () => {
      admissionGeneration.current += 1;
    },
    [],
  );
  const startInstall = useCallback(
    async (options?: {
      guardrail?: ServerGuardrailState;
    }): Promise<string[]> => {
      if (
        !canInstall ||
        admissionInFlight.current ||
        (phaseRef.current !== "configure" && phaseRef.current !== "guardrails")
      ) {
        return [];
      }

      const generation = admissionGeneration.current;
      admissionInFlight.current = true;
      try {
        if (beforeInstall && !(await beforeInstall())) return [];
        if (generation !== admissionGeneration.current) return [];
      } finally {
        admissionInFlight.current = false;
      }

      // Configs without a compatible endpoint can't be installed; report them as
      // failed instead of blocking the rest of the batch (or, for headless
      // callers, stalling forever with nothing to install).
      const targets = serverConfigs
        .filter((config) => config.remotes.length > 0)
        .flatMap(buildInstallTargets);
      const uninstallable = serverConfigs.filter(
        (config) => config.remotes.length === 0,
      );

      setPhase("installing");
      setStatuses([
        ...targets.map((target, index) => ({
          key: `${index}-${target.remote.url}`,
          name: target.name,
          server: target.server,
          status: "pending" as const,
        })),
        ...uninstallable.map((config, index) => ({
          key: `uninstallable-${index}`,
          name: config.name,
          status: "failed" as const,
          error:
            "This server does not expose a compatible remote endpoint and cannot be added.",
        })),
      ]);

      const reqOpts = projectSlug
        ? { headers: { "gram-project": projectSlug } }
        : undefined;

      const setStatusAt = (
        index: number,
        updates: Partial<ServerInstallStatus>,
      ) => {
        setStatuses((prev) =>
          prev.map((status, i) =>
            i === index ? { ...status, ...updates } : status,
          ),
        );
      };

      let anyAuthConfigured = false;
      let anyUnproxiedInstalled = false;
      // Servers that were created, for scoping the guardrail. A server kept
      // disabled for identity setup still exists and is still worth guarding.
      const installedServerIds: string[] = [];
      const iconPersistences: Promise<boolean>[] = [];
      for (const [index, target] of targets.entries()) {
        setStatusAt(index, { status: "creating" });
        try {
          const result = await installTarget(target, reqOpts);
          iconPersistences.push(result.iconPersistence);
          anyAuthConfigured ||= result.authConfigured;
          anyUnproxiedInstalled ||= isFigmaCatalogServer(target.server);
          // Unproxied servers never pass through Speakeasy, so there is no traffic
          // for a guardrail to inspect.
          if (!isFigmaCatalogServer(target.server)) {
            installedServerIds.push(result.mcpServer.id);
          }
          const identitySetupRequired =
            result.identityConfiguration?.status === "setup-required";
          setStatusAt(index, {
            status: identitySetupRequired ? "failed" : "completed",
            identityMode: target.identityMode,
            remoteUrl: target.remote.url,
            mcpServerId: result.mcpServer.id,
            mcpServerParam: mcpServerRouteParam(result.mcpServer),
            mcpEndpointUrl: result.mcpEndpointUrl,
            error:
              result.identityConfiguration?.status === "setup-required"
                ? `Server retained disabled. ${result.identityConfiguration.message}`
                : undefined,
            setupRequired:
              result.identityConfiguration?.status === "setup-required"
                ? result.identityConfiguration.message
                : undefined,
          });
        } catch (err) {
          setStatusAt(index, {
            status: "failed",
            error: err instanceof Error ? err.message : String(err),
          });
        }
      }

      // refetchType "all" forces the refetch even when there are no active
      // observers, so list pages pick up the new servers on next mount.
      const invalidations = [
        invalidateAllRemoteMcpServers(queryClient, { refetchType: "all" }),
        invalidateAllRemoteMcpServerHeaders(queryClient, {
          refetchType: "all",
        }),
        invalidateAllMcpServers(queryClient, { refetchType: "all" }),
        invalidateAllMcpEndpoints(queryClient, { refetchType: "all" }),
        // Installs may have persisted a catalog icon into MCP metadata.
        invalidateAllGetMcpMetadata(queryClient, { refetchType: "all" }),
        // Every create links a fresh user_session_issuer.
        invalidateAllUserSessionIssuers(queryClient, { refetchType: "all" }),
      ];
      // The issuer/client caches only change when auto-configuration actually
      // ran to completion on at least one server.
      if (anyAuthConfigured) {
        invalidations.push(
          invalidateAllRemoteSessionIssuers(queryClient, {
            refetchType: "all",
          }),
          invalidateAllRemoteSessionClients(queryClient, {
            refetchType: "all",
          }),
        );
      }
      // Unproxied installs (e.g. Figma) don't touch any of the remote-server
      // caches above, so the Sources page's unproxied listing needs its own
      // invalidation.
      if (anyUnproxiedInstalled) {
        invalidations.push(
          invalidateAllUnproxiedMcpServers(queryClient, { refetchType: "all" }),
        );
      }
      await Promise.all(invalidations);

      // The guardrail is created only after the servers exist, and only covers
      // the ones that installed. A failure leaves the servers in place and is
      // reported on the Complete phase, where it can be retried from each
      // server's Guardrails tab.
      if (options?.guardrail) {
        const installedIds = installedServerIds;
        const name = guardrailNameFor(targets.map((target) => target.name));
        if (installedIds.length === 0) {
          setGuardrailOutcome({
            status: "failed",
            name,
            error:
              "No server that Speakeasy proxies was added, so there was nothing to scope it to.",
          });
        } else {
          try {
            await client.risk.policies.create(
              {
                createRiskPolicyRequestBody: buildServerGuardrailRequest(
                  options.guardrail,
                  { mcpServerIds: installedIds, name, mode: detectorMode },
                ),
              },
              undefined,
              reqOpts,
            );
            setGuardrailOutcome({ status: "created", name });
          } catch (err) {
            setGuardrailOutcome({
              status: "failed",
              name,
              error: err instanceof Error ? err.message : String(err),
            });
          }
          // Outside the create's error path: a failed refetch must not report
          // a policy that exists as not created.
          void invalidateAllRiskListPolicies(queryClient, {
            refetchType: "all",
          }).catch(() => undefined);
        }
      }

      // Icon persistence runs detached from the install loop, so the metadata
      // invalidation above usually fires before the logos land. Refetch again
      // once they settle — without gating the "complete" transition on it. The
      // persistence promises never reject, so Promise.all is safe here.
      void Promise.all(iconPersistences).then((persisted) => {
        if (persisted.some(Boolean)) {
          return invalidateAllGetMcpMetadata(queryClient, {
            refetchType: "all",
          });
        }
        return undefined;
      });

      setPhase("complete");
      return installedServerIds;
    },
    [
      beforeInstall,
      canInstall,
      client,
      detectorMode,
      installTarget,
      projectSlug,
      queryClient,
      serverConfigs,
    ],
  );

  const continueToGuardrails = useCallback(
    (options?: { configureSkipped?: boolean }) => {
      if (!canInstall || phaseRef.current !== "configure") return;
      setConfigureSkipped(options?.configureSkipped === true);
      setGuardrail(catalogPresetState([]));
      setPhase("guardrails");
    },
    [canInstall],
  );

  const backToConfigure = useCallback(() => {
    setPhase("configure");
  }, []);

  const reset = useCallback(() => {
    admissionGeneration.current += 1;
    setStatuses([]);
    setGuardrail(null);
    setConfigureSkipped(false);
    setGuardrailOutcome(undefined);
    partitionServers();
  }, [partitionServers]);

  const base: WorkflowBase = {
    projectSlug,
    isServerAlreadyInstalled,
    reset,
  };

  switch (phase) {
    case "selectRemotes":
      return {
        phase,
        multiRemoteConfigs,
        currentServerIndex,
        updateCurrentConfig,
        nextServer,
        canProceed,
        ...base,
      };
    case "configure":
      return {
        phase,
        serverConfigs,
        updateServerConfig,
        setHeaderValue,
        canInstall,
        installBlockedReason,
        startInstall: async () => {
          await startInstall();
        },
        goBack: hasMultiRemoteServers ? goBack : undefined,
        continueToGuardrails: offerGuardrails
          ? continueToGuardrails
          : undefined,
        ...base,
      };
    case "guardrails":
      return {
        phase,
        guardrail: guardrail ?? catalogPresetState([]),
        updateGuardrail: (update) =>
          setGuardrail((prev) => update(prev ?? catalogPresetState([]))),
        servers: serverConfigs
          .filter((config) => !isFigmaCatalogServer(config.server))
          .map(guardrailServerSummary),
        installWithGuardrail: async () => {
          await startInstall({
            guardrail: guardrail ?? catalogPresetState([]),
          });
        },
        skip: async () => {
          await startInstall();
        },
        installForCustomizing: () => startInstall(),
        goBack: configureSkipped ? undefined : backToConfigure,
        ...base,
      };
    case "installing":
      return { phase, statuses, ...base };
    case "complete":
      return { phase, statuses, guardrail: guardrailOutcome, ...base };
  }
}

function guardrailServerSummary(config: ServerConfig): GuardrailServerSummary {
  return {
    key: config.server.registrySpecifier,
    name: config.name,
    registrySpecifier: config.server.registrySpecifier,
    ...(config.server.iconUrl ? { iconUrl: config.server.iconUrl } : {}),
    oauth: supportsAutomaticOAuth(config.server),
  };
}

/** One guardrail covers every server in the install, so the name names the
 *  install rather than a single server. */
function guardrailNameFor(serverNames: string[]): string {
  const [first] = serverNames;
  if (first === undefined) return "Catalog guardrail";
  return serverNames.length === 1
    ? `${first} guardrail`
    : `${first} and ${serverNames.length - 1} more guardrail`;
}
